/**
 * SdmsBatchResultsPopup — the SDMS ribbon's "QC Batch Results" action.
 *
 * OG (`SpreadsheetEntryController.cs` :9301, `QCbatchIDResults`) opens a modal
 * list of the batch's per-(sample × analyte) results (`SDMSDCAB_ListView_Copy`).
 * Here it's a read-only popup fed by the SAME endpoint the Result-Validation
 * package view already uses — `GET …/result-validation/{oid}/batch-results` —
 * so there's one source for batch results, not two (CLAUDE.md rule 8).
 *
 * Read-only by design: this is Phase 1 (view only). The editable Batch-Results
 * grid + save lives in the Result-Validation flow; entering/saving results from
 * SDMS itself is a later phase.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { useNotify } from '../../components/notifications';
import { qcBatchApi, type SdmsBatchResult } from './qcBatch.api';
import './sdmsEntry.css';

export function SdmsBatchResultsPopup({ batchOid, batchLabel, onClose }:
  { batchOid: string; batchLabel: string; onClose: () => void }) {
  const notify = useNotify();
  const [rows, setRows] = useState<SdmsBatchResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    qcBatchApi.sdmsBatchResults(batchOid)
      .then(r => { if (!cancelled) setRows(r); })
      .catch(() => { if (!cancelled) notify.error('Could not load the batch results.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchOid]);

  // Close on Escape / backdrop click, like the other SDMS overlays.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const columns = useMemo<AppColumn<SdmsBatchResult>[]>(() => [
    { field: 'sys',           header: 'SysSampleCode' },
    { field: 'parameter',     header: 'Parameter' },
    { field: 'numericResult', header: 'Numeric Result' },
    { field: 'result',        header: 'Result' },
    { field: 'units',         header: 'Units' },
    { field: 'df',            header: 'DF' },
    { field: 'rptLimit',      header: 'Rpt Limit' },
    { field: 'mdl',           header: 'MDL' },
    { field: 'loq',           header: 'LOQ' },
    { field: 'spikeAmount',   header: 'Spike Amount' },
    { field: 'analyzedBy',    header: 'Analyzed By' },
    { field: 'analyzedDate',  header: 'Analyzed Date', type: 'datetime' },
    { field: 'sampleName',    header: 'Sample Name' },
    { field: 'client',        header: 'Client' },
    { field: 'projectId',     header: 'Project ID' },
  ], []);

  return createPortal(
    <div className="sdms-brp-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sdms-brp" role="dialog" aria-label="QC Batch Results">
        <div className="sdms-brp-hd">
          <span>QC Batch Results — {batchLabel}</span>
          <button className="sdms-brp-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="sdms-brp-body">
          <AppDataGrid<SdmsBatchResult>
            rows={rows}
            columns={columns}
            getRowId={r => r.id ?? `${r.sys}|${r.parameter}`}
            loading={loading}
            emptyText="No results for this batch yet."
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
